import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { AdminUserCallsCard } from '@/features/admin/user-record/AdminUserCallsCard'
import { UserBreadcrumbs } from '@/features/admin/user-record/UserBreadcrumbs'
import {
  getOffsetYear,
  getPastAllUserCallEventsIds,
} from '@/selectors/userCallEventSelectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { adminFetchSingleUserIfNeeded } from '@/actions/admin/adminAllUsersActions'
import { fetchAdminUserCallEventsIfNeeded } from '@/actions/admin/userCallEventsActions'
import { useReselector } from '@/utils/sharedHooks'
import { Tabs, Separator } from '@/ui'

export const UserCallsPanel = () => {
  const dispatch = useAppDispatch()
  const { userId } = useParams<{ userId: string }>()
  const numericUserId = Number(userId)

  const pastYearCallEventIds = useReselector(
    getPastAllUserCallEventsIds,
    numericUserId
  )

  useEffect(() => {
    dispatch(fetchAdminUserCallEventsIfNeeded(numericUserId))
    dispatch(adminFetchSingleUserIfNeeded(numericUserId))
  }, [dispatch, numericUserId])

  const tabs = useMemo(() => {
    const currentYear = getOffsetYear()

    const baseTabs = [
      {
        value: 'current',
        label: `${currentYear} Checkins`,
        content: (
          <AdminUserCallsCard userId={numericUserId} showPastLink={false} />
        ),
      },
    ]

    if (pastYearCallEventIds.length) {
      baseTabs.push({
        value: 'prior',
        label: 'Prior Year Checkins',
        content: (
          <AdminUserCallsCard
            userId={numericUserId}
            showPastEvents
            showPastLink={false}
          />
        ),
      })
    }

    return baseTabs
  }, [pastYearCallEventIds.length, numericUserId])

  return (
    <div id="adminPanel" className="p-4 md:p-6 space-y-4">
      <UserBreadcrumbs userId={numericUserId} pageName="All User Calls" />
      <Separator />
      <Tabs defaultValue={tabs[0].value} className="w-full">
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Trigger key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Content key={tab.value} value={tab.value}>
            {tab.content}
          </Tabs.Content>
        ))}
      </Tabs>
    </div>
  )
}
